import React, { useState } from 'react';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import { createGroupInviteLink } from '../../api/groupInviteLinkService';
import { buildJoinLeagueUrl } from '../../utils/joinLeagueIntent';
import { typography } from '../../design-system/tokens';

export default function ShareLeagueLinkCard({ groupId, groupName }) {
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const ensureInviteUrl = async () => {
    if (inviteUrl) return inviteUrl;
    setLoading(true);
    setError('');
    try {
      const data = await createGroupInviteLink(groupId);
      const url = buildJoinLeagueUrl(data.token);
      setInviteUrl(url);
      return url;
    } catch (err) {
      setError(err?.message || 'Could not create invite link');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      const url = await ensureInviteUrl();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      /* ensureInviteUrl sets error */
    }
  };

  const handleShare = async () => {
    try {
      const url = await ensureInviteUrl();
      if (typeof navigator.share === 'function') {
        await navigator.share({
          title: `Join ${groupName || 'my league'} on LockedIn`,
          text: 'Join our private league — tap the link to get in.',
          url,
        });
        return;
      }
      await handleCopy();
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err?.message || 'Share failed');
      }
    }
  };

  return (
    <div className="rounded-xl border border-violet-500/25 bg-violet-950/20 p-4 space-y-3">
      <div>
        <p className={`${typography.microWide} text-violet-300`}>Share invite link</p>
        <p className="text-xs text-gray-400 mt-1 font-medium leading-relaxed">
          Friends can join without knowing your arena name. Link expires in 7 days.
        </p>
      </div>
      {error && <Alert tone="error" className="text-xs font-bold">{error}</Alert>}
      {inviteUrl && (
        <p className="text-[10px] text-gray-500 break-all font-mono bg-black/40 rounded-lg px-2 py-1.5">
          {inviteUrl}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="admin"
          disabled={loading}
          onClick={handleShare}
        >
          {loading ? 'Preparing…' : 'Share link'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={loading}
          onClick={handleCopy}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
      </div>
    </div>
  );
}
